// Every themed token from a variant's seeds (spec web/themes-engine). The result has tokens.ts's
// names: `Themed` is every per-scheme token but the kept ones, so a token added to tokens.ts fails
// tsc here until it is derived or kept.
import { darkTokens, lightTokens, shared } from "../tokens";
import { contrast, darken, lighten, mix, withAlpha } from "./color";
import type { Scheme, Seeds } from "./family";

export type TokenName = keyof typeof lightTokens;

/**
 * Tokens no family changes: the video editor's timeline and stage, which draw live's editor and
 * keep today's values in each scheme (decision 3), and the achievements' tiers, each a material's
 * colour (spec web/achievements).
 */
export const KEPT_TOKENS = [
  "--cai-color-track-video",
  "--cai-color-track-video-ink",
  "--cai-color-track-video-border",
  "--cai-color-track-text",
  "--cai-color-track-text-ink",
  "--cai-color-track-text-border",
  "--cai-color-track-effect",
  "--cai-color-track-effect-ink",
  "--cai-color-track-effect-border",
  "--cai-color-track-caption",
  "--cai-color-track-caption-ink",
  "--cai-color-track-caption-border",
  "--cai-color-track-selected",
  "--cai-color-canvas-guide",
  "--cai-color-track-band",
  "--cai-color-stage",
  "--cai-color-track-rule",
  "--cai-tier-extra",
  "--cai-tier-supporting-role",
  "--cai-tier-leading-role",
  "--cai-tier-directors-cut",
  "--cai-tier-cult-classic",
] as const satisfies readonly TokenName[];

type Themed = Exclude<TokenName, (typeof KEPT_TOKENS)[number] | keyof typeof shared>;

export function deriveTokens(s: Seeds, scheme: Scheme): Record<TokenName, string> {
  const dark = scheme === "dark";
  /** `weight` of `color` over the surface: a soft fill of that colour. */
  const tint = (color: string, weight: number) => mix(color, s.surface, weight);
  /** The primary moved away from its label's colour, so a hover or press only gains contrast. */
  const lighterIsClearer =
    contrast(s.onPrimary, lighten(s.primary, 0.5)) >= contrast(s.onPrimary, s.primary);
  const awayFromLabel = (amount: number) =>
    lighterIsClearer ? lighten(s.primary, amount) : darken(s.primary, amount);
  const themed: Record<Themed, string> = {
    "--cai-color-canvas": s.surface,
    "--cai-color-page": s.page,
    "--cai-color-surface": s.surface,
    "--cai-color-surface-hover": s.hover,
    "--cai-color-skeleton": tint(s.text, 0.14),
    "--cai-color-glass": withAlpha(s.surface, dark ? 0.85 : 0.95),
    "--cai-color-primary": s.primary,
    "--cai-color-primary-hover": awayFromLabel(0.14),
    "--cai-color-primary-pressed": awayFromLabel(0.28),
    "--cai-color-primary-soft": tint(s.primary, dark ? 0.16 : 0.08),
    "--cai-color-primary-soft-hover": tint(s.primary, dark ? 0.24 : 0.14),
    "--cai-color-primary-deep": dark ? lighten(s.textPrimary, 0.8) : darken(s.primary, 0.6),
    "--cai-color-on-primary": s.onPrimary,
    "--cai-color-text": s.text,
    "--cai-color-text-strong": s.strong,
    "--cai-color-text-heading": s.strong,
    "--cai-color-text-muted": s.muted,
    "--cai-color-text-secondary": s.strong,
    "--cai-color-text-primary": s.textPrimary,
    "--cai-color-text-link": s.link,
    "--cai-color-text-disabled": tint(s.muted, 0.55),
    "--cai-color-text-inverse": dark ? s.surface : s.page,
    "--cai-color-text-danger": s.danger,
    "--cai-color-text-warning": s.warning,
    "--cai-color-text-success": s.success,
    "--cai-color-text-info": s.info,
    "--cai-color-border": s.border,
    "--cai-color-border-subtle": tint(s.border, 0.5),
    "--cai-color-border-primary": dark ? s.textPrimary : s.primary,
    "--cai-color-border-primary-soft": tint(s.primary, 0.3),
    "--cai-color-border-active": tint(s.primary, 0.6),
    "--cai-color-icon": s.text,
    "--cai-color-icon-muted": s.muted,
    "--cai-color-icon-disabled": tint(s.muted, 0.55),
    "--cai-color-theme-icon": dark ? s.text : s.textPrimary,
    "--cai-color-interactive-hover": tint(s.primary, dark ? 0.14 : 0.08),
    "--cai-color-interactive-active": tint(s.primary, dark ? 0.22 : 0.14),
    "--cai-color-interactive-disabled": dark ? s.hover : s.border,
    "--cai-color-outlined-hover": dark ? s.hover : tint(s.primary, 0.08),
    "--cai-color-outlined-disabled": dark ? s.hover : "transparent",
    "--cai-color-filled-disabled": withAlpha(s.muted, dark ? 0.15 : 0.1),
    "--cai-color-success": s.success,
    "--cai-color-success-soft": tint(s.success, 0.14),
    "--cai-color-warning": s.warning,
    "--cai-color-warning-soft": tint(s.warning, 0.14),
    "--cai-color-danger": s.danger,
    "--cai-color-danger-soft": tint(s.danger, 0.14),
    "--cai-color-info-soft": tint(s.info, 0.14),
    "--cai-color-failed": tint(s.primary, dark ? 0.16 : 0.08),
    "--cai-color-disabled": dark ? s.hover : tint(s.border, 0.4),
    "--cai-color-loading": tint(s.primary, 0.04),
    "--cai-color-pane": dark ? s.page : tint(s.border, 0.3),
    "--cai-color-cover-fallback": dark ? s.hover : s.muted,
    "--cai-color-tag": withAlpha(s.muted, dark ? 0.15 : 0.1),
    "--cai-color-auth-card": s.surface,
    "--cai-color-auth-field": dark ? withAlpha(s.text, 0.1) : s.page,
    "--cai-color-auth-secondary": dark ? withAlpha(s.text, 0.1) : "transparent",
    "--cai-color-auth-border": dark ? "transparent" : tint(s.border, 0.5),
    "--cai-color-auth-divider": dark ? withAlpha(s.text, 0.1) : tint(s.border, 0.5),
    "--cai-color-shadow": withAlpha(dark ? s.primary : s.strong, 0.1),
    "--cai-color-ring": s.ring,
    "--cai-gradient-from": s.from,
    "--cai-gradient-to": s.to,
    "--cai-gradient-page": `linear-gradient(115deg, ${mix(s.from, s.page, 0.07)} 0%, ${s.page} 50%, ${mix(s.to, s.page, 0.07)} 100%)`,
    "--cai-gradient-slot": `linear-gradient(133.36deg, ${withAlpha(s.from, dark ? 0.15 : 0.1)} 2.7%, ${withAlpha(s.to, dark ? 0.15 : 0.1)} 100%)`,
    "--cai-color-card-border": dark ? "transparent" : tint(s.border, 0.5),
    "--cai-shadow-card-hover": dark ? "none" : `0 10px 20px 0 ${withAlpha(s.strong, 0.1)}`,
  };
  return { ...(dark ? darkTokens : lightTokens), ...themed };
}
